/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useIsAuthenticated from 'modules/common/auth/hooks/use-is-authenticated';
import useIsTokenValid from 'modules/common/auth/hooks/use-access-token-validity';
import { selectIsSessionTimedOut, selectEmbedTokenExpiry, selectTokenRevocation } from 'modules/dashboard/selectors';
import { dashboardActions } from 'modules/dashboard/slice';
import { selectAccessToken, selectAuthLoading, selectOrganizationId, selectRefreshToken } from '../auth/selectors';
import { authActions } from '../auth/slice';
import TIME_OUTS from '../constants/time-outs';
/**
 * Based on refresh token get the new access token
 */
const useUpdateToken = () => {
  const dispatch = useDispatch();
  //
  const refreshToken = useSelector(selectRefreshToken);
  const accessToken = useSelector(selectAccessToken);
  const pbiTokenExpiry = useSelector(selectEmbedTokenExpiry);
  const organizationId = useSelector(selectOrganizationId);
  const isSessionTimedOut = useSelector(selectIsSessionTimedOut);
  const isAuthLoading = useSelector(selectAuthLoading);
  const isTokenRevoked = useSelector(selectTokenRevocation);
  const { checkAuthentication } = useIsAuthenticated();
  const { doNeedRefresh } = useIsTokenValid();
  //
  const tryUpdateToken = useCallback(() => {
    dispatch(authActions.refreshToken({ refreshToken }));
  }, [refreshToken]);
  //
  const tryUpdatePBIToken = useCallback(() => {
    dispatch(
      dashboardActions.getEmbedToken({
        organizationId,
      })
    );
  }, [organizationId]);
  //
  const doNeedRefreshPbiToken = (exp) => {
    if (exp) {
      const expireTime = new Date(exp);
      const currentTime = new Date();
      return expireTime.getTime() <= currentTime.getTime();
    }
    return false;
  };
  //
  useEffect(() => {
    // session observer
    const interval = setInterval(() => {
      if (checkAuthentication(refreshToken)) {
        // check whether access token need to refresh
        if (doNeedRefresh(accessToken)) {
          if (!isSessionTimedOut && !isTokenRevoked && !isAuthLoading) {
            tryUpdateToken();
          }
        }
        // check whether pbi embed token need to refresh
        if (doNeedRefreshPbiToken(pbiTokenExpiry)) {
          tryUpdatePBIToken();
        }
        // session has been timed out
      } else if (!isSessionTimedOut) {
        dispatch(dashboardActions.setSessionTimeOut(true));
      }
    }, TIME_OUTS.SESSION_OBSERVER_TIME);

    return () => {
      clearInterval(interval);
    };
  }, [isSessionTimedOut, isAuthLoading, isTokenRevoked, accessToken, pbiTokenExpiry, refreshToken]);
};
//
export default useUpdateToken;
